import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { Entry } from '../EntryListItem/Entry'

import './TrafficEntry.module.sass'

import entryDetailsOpenAtom from '../../recoil/entryDetailsOpen/atom'
import focusedItemAtom from '../../recoil/focusedItem/atom'
import focusedRecordAtom from '../../recoil/focusedRecord/atom'
import focusedStreamAtom from '../../recoil/focusedStream/atom'
import entryDetailsViewModeAtom from '../../recoil/entryDetailsViewMode'
import timezoneAtom from '../../recoil/timezone/atom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'


import Protocol from '../UI/Protocol/Protocol'
import StatusCode from '../UI/StatusCode/StatusCode'
import { TlsIndicator } from './components/TlsIndicator'
import { resolveTrafficEntryStyles } from './helpers'
import { Method } from './components/Method'
import { Route } from './components/Route'
import { FullResolutionData } from './components/FullResolutionData/FullResolutionData'
import { IpWithPort } from './components/IpWithPort/IpWithPort'
import { GenericDirectionIcon } from '../EntryListItem/icons/GenericDirectionIcon'
import { Timestamp } from './components/Timestamp'
import useWindowDimensions from '../../hooks/WindowDimensionsHook'
import { DuplicateBadge } from './components/DuplicateBadge/DuplicateBadge'
import focusedEntryAtom from '../../recoil/focusedEntry'
import classNames from 'classnames'
import { CaptureBackend } from './components/CaptureBackend'
import variables from '../../variables.module.scss'
import { Bandwidth, TotalBandwidth } from './components/Bandwidth/Bandwidth'
import useStreamingList from '../TrafficViewer/useStreamingList'
import { RecordedBadge } from './components/RecordedBadge'
import { SessionStorageKey } from '../../consts'
import { TrafficWebSocketStreamingOption, WsOptionsNoClickAwayClassName } from '../Filters/WebsocketOptionsDropdown'
import websocketOptionsDropdownOpenAtom from '../../recoil/websocketOptionsDropdownOpen'
import useSessionStorageState from 'use-session-storage-state'
import { Latency } from './components/Latency'
import { Namespaces } from './components/Namespaces'
import { appDetailedStreamingEnabled } from '../../types/global'

interface TrafficEntryProps {
  entry: Entry
  duplicateOrigin?: Entry
  headingMode?: boolean
  style?: unknown
}

export const TrafficEntry: React.FC<TrafficEntryProps> = ({
  entry,
  duplicateOrigin = null,
  headingMode = false,
  style
}) => {
  const [wsOption] = useSessionStorageState(
    SessionStorageKey.TrafficWebSocketStreamingOption,
    {
      defaultValue: appDetailedStreamingEnabled() ?
        TrafficWebSocketStreamingOption.Detailed :
        TrafficWebSocketStreamingOption.FlowsOnly
    }
  )

  const setWsOptionsDropdownOpen = useSetRecoilState(
    websocketOptionsDropdownOpenAtom
  )

  const setFocusedEntry = useSetRecoilState(focusedEntryAtom)
  const [focusedItem, setFocusedItem] = useRecoilState(focusedItemAtom)
  const [focusedStream, setFocusedStream] = useRecoilState(focusedStreamAtom)
  const [entryDetailsOpen, setEntryDetailsOpen] =
    useRecoilState(entryDetailsOpenAtom)
  const setFocusedRecord = useSetRecoilState(focusedRecordAtom)

  const entryDetailsViewMode = useRecoilValue(entryDetailsViewModeAtom)

  const [showLongNames, setShowLongNames] = useState(false)

  const { setToBottom } = useStreamingList()

  useEffect(() => {
    setShowLongNames(
      !entryDetailsOpen ||
        (entryDetailsOpen && entryDetailsViewMode === 'drawer') ||
        wsOption === TrafficWebSocketStreamingOption.FlowsOnly
    )
  }, [entryDetailsOpen, entryDetailsViewMode, wsOption])

  const handleFocusOnEntry = useCallback(() => {
    if (wsOption === TrafficWebSocketStreamingOption.FlowsOnly) {
      // show modal "Enable Detailed streaming option to see details"
      return
    }

    if (wsOption === TrafficWebSocketStreamingOption.Detailed) {
      setToBottom(false)
      setFocusedEntry(entry)
      setFocusedItem(entry.id)
      setFocusedStream(entry.stream)
      setFocusedRecord(entry.record)
      setEntryDetailsOpen(true)
    }
  }, [entry, wsOption])

  const handleOpenWsOptionsDropdown = () => {
    setWsOptionsDropdownOpen(true)
  }

  const isSelected = focusedItem === entry.id

  const timezone = useRecoilValue(timezoneAtom)

  const { width: windowWidth } = useWindowDimensions()

  const trafficEntryClasses = classNames(
    'TrafficEntry',
    {
      selected: isSelected,
      error: entry.error
    },
    WsOptionsNoClickAwayClassName
  )

  return (
    <Box
      id={entry.key}
      className={trafficEntryClasses}
      style={resolveTrafficEntryStyles(
        entry,
        focusedItem,
        focusedStream,
        headingMode,
        style
      )}
      boxShadow={!headingMode ? variables.lightShadowBottom : null}
      onClick={
        wsOption === TrafficWebSocketStreamingOption.FlowsOnly
          ? handleOpenWsOptionsDropdown
          : handleFocusOnEntry
      }
    >
      {!headingMode && (
        <Protocol
          protocol={entry.proto}
          horizontal={false}
          height='62px'
          error={entry.error !== null}
        />
      )}
      <TlsIndicator tls={entry.tls} />
      <Box className='TrafficEntryResolution'>
        <Box boxSizing='border-box' display='flex' alignItems='center' gap='8px' sx={{
          marginTop: '0px',
          marginBottom: '12px',
        }}>
          <Method name={entry.method} query={entry.methodQuery} />
          <StatusCode
            statusCode={entry?.status}
            statusQuery={entry.statusQuery}
            protocol={entry.proto.name}
          />
          {!entry?.requestSize && !entry?.responseSize && entry?.size !== 0 && (
            <Bandwidth type={TotalBandwidth} size={entry.size} />
          )}
          {wsOption === TrafficWebSocketStreamingOption.FlowsOnly && (
            <Latency microseconds={entry.elapsedTime} />
          )}
          <Namespaces
            headingMode={headingMode}
            entryDetailsOpen={entryDetailsOpen}
            windowWidth={windowWidth}
            wsOption={wsOption}
            src={entry.src}
            error={entry.error}
            proto={entry.proto}
            dst={entry.dst}
            tooltipInverted
          />
          <DuplicateBadge
            duplicate={entry.duplicate !== ''}
            duplicateOrigin={duplicateOrigin}
          />
          <CaptureBackend capture={entry.capture} />
          <RecordedBadge recordName={entry.record} />
          <Route
            name={entry.summary}
            truncateName={!showLongNames && !headingMode}
            query={entry.summaryQuery}
          />
        </Box>
        <FullResolutionData
          entry={entry}
          showLongNames={showLongNames}
          headingMode={headingMode}
        />
      </Box>
      <Box
        position='absolute'
        right={headingMode ? '45px' : '15px'}
        display='flex'
        alignItems='center'
      >
        <Box className='TrafficEntryIps' sx={{
          marginRight: (!entryDetailsOpen || windowWidth > 1760) ? '20px' : null,
        }}>
          <IpWithPort ip={entry.src.ip} port={entry.src.port} point='src' />
          <GenericDirectionIcon
            fill={
              entry.error ? variables.failureColor : entry.proto.backgroundColor
            }
          />
          <IpWithPort ip={entry.dst.ip} port={entry.dst.port} point='dst' />
        </Box>
        {(!entryDetailsOpen || windowWidth > 1760) && (
          <>
            <Box className='TimestampDivider'></Box>
            <Timestamp unixTimestamp={entry.timestamp} timezone={timezone} />
          </>
        )}
      </Box>
    </Box>
  )
}
